import { Node, XYPosition } from '@xyflow/react';

export interface HelperLine {
  position: number;
  orientation: 'horizontal' | 'vertical';
}

export interface HelperLinesResult {
  horizontal?: HelperLine;
  vertical?: HelperLine;
  snapPosition: XYPosition;
}

// Distance threshold for snapping (in pixels)
const SNAP_DISTANCE = 5;

/**
 * Get the alignment helper lines for a node being dragged
 * @param draggingNode The node being dragged
 * @param nodes All nodes in the flow
 * @param nodeWidth Width of the dragging node
 * @param nodeHeight Height of the dragging node
 * @returns Helper lines and snap position
 */
export function getHelperLines(
  draggingNode: Node,
  nodes: Node[],
  nodeWidth: number,
  nodeHeight: number
): HelperLinesResult {
  const otherNodes = nodes.filter((n) => n.id !== draggingNode.id);
  
  const draggingNodeLeft = draggingNode.position.x;
  const draggingNodeRight = draggingNode.position.x + nodeWidth;
  const draggingNodeCenterX = draggingNode.position.x + nodeWidth / 2;
  const draggingNodeTop = draggingNode.position.y;
  const draggingNodeBottom = draggingNode.position.y + nodeHeight;
  const draggingNodeCenterY = draggingNode.position.y + nodeHeight / 2;

  let horizontalLine: HelperLine | undefined;
  let verticalLine: HelperLine | undefined;
  let snapX = draggingNode.position.x;
  let snapY = draggingNode.position.y;

  // Check alignment with other nodes
  for (const node of otherNodes) {
    const nodeWidth = (node.measured?.width || node.width) ?? 0;
    const nodeHeight = (node.measured?.height || node.height) ?? 0;
    
    const nodeLeft = node.position.x;
    const nodeRight = node.position.x + nodeWidth;
    const nodeCenterX = node.position.x + nodeWidth / 2;
    const nodeTop = node.position.y;
    const nodeBottom = node.position.y + nodeHeight;
    const nodeCenterY = node.position.y + nodeHeight / 2;

    // Vertical alignment checks (horizontal lines)
    // Top alignment
    if (Math.abs(draggingNodeTop - nodeTop) < SNAP_DISTANCE) {
      horizontalLine = { position: nodeTop, orientation: 'horizontal' };
      snapY = nodeTop;
    }
    // Center alignment
    else if (Math.abs(draggingNodeCenterY - nodeCenterY) < SNAP_DISTANCE) {
      horizontalLine = { position: nodeCenterY, orientation: 'horizontal' };
      snapY = nodeCenterY - nodeHeight / 2;
    }
    // Bottom alignment
    else if (Math.abs(draggingNodeBottom - nodeBottom) < SNAP_DISTANCE) {
      horizontalLine = { position: nodeBottom, orientation: 'horizontal' };
      snapY = nodeBottom - nodeHeight;
    }

    // Horizontal alignment checks (vertical lines)
    // Left alignment
    if (Math.abs(draggingNodeLeft - nodeLeft) < SNAP_DISTANCE) {
      verticalLine = { position: nodeLeft, orientation: 'vertical' };
      snapX = nodeLeft;
    }
    // Center alignment
    else if (Math.abs(draggingNodeCenterX - nodeCenterX) < SNAP_DISTANCE) {
      verticalLine = { position: nodeCenterX, orientation: 'vertical' };
      snapX = nodeCenterX - nodeWidth / 2;
    }
    // Right alignment
    else if (Math.abs(draggingNodeRight - nodeRight) < SNAP_DISTANCE) {
      verticalLine = { position: nodeRight, orientation: 'vertical' };
      snapX = nodeRight - nodeWidth;
    }

    // Stop checking if we found both lines
    if (horizontalLine && verticalLine) {
      break;
    }
  }

  return {
    horizontal: horizontalLine,
    vertical: verticalLine,
    snapPosition: { x: snapX, y: snapY },
  };
}

/**
 * Apply snapping to a node's position
 * @param position The current position
 * @param nodes All nodes in the flow
 * @param draggingNodeId The ID of the node being dragged
 * @param nodeWidth Width of the dragging node
 * @param nodeHeight Height of the dragging node
 * @returns Snapped position
 */
export function applySnapToPosition(
  position: XYPosition,
  nodes: Node[],
  draggingNodeId: string,
  nodeWidth: number,
  nodeHeight: number
): XYPosition {
  const draggingNode = nodes.find((n) => n.id === draggingNodeId);
  if (!draggingNode) {
    return position;
  }

  const tempNode = { ...draggingNode, position };
  const result = getHelperLines(tempNode, nodes, nodeWidth, nodeHeight);
  
  return result.snapPosition;
}
